import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
  FolderOpen, ArrowRight, Clock, CheckCircle,
  AlertTriangle, Search, Package
} from 'lucide-react';
import { format, parseISO, isValid, isPast } from 'date-fns';

const safeDate = (d) => {
  try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy') : '-'; }
  catch { return '-'; }
};

const statusConfig = {
  active:    { label: 'Active',    bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  completed: { label: 'Completed', bg: 'bg-blue-100',    text: 'text-blue-700',    dot: 'bg-blue-500'    },
  on_hold:   { label: 'On Hold',   bg: 'bg-amber-100',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
  cancelled: { label: 'Cancelled', bg: 'bg-red-100',     text: 'text-red-700',     dot: 'bg-red-500'     },
};

export default function ClientProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/client/projects')
      .then(r => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = projects.filter(p => {
    const matchSearch = !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.project_id && p.project_id.toLowerCase().includes(search.toLowerCase()));
    const matchFilter = filter === 'all' || p.status === filter;
    return matchSearch && matchFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Projects</h1>
        <p className="text-gray-500 text-sm mt-1">Track the status and progress of your orders</p>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by project name or ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
          />
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white min-w-[140px]"
        >
          <option value="all">All Projects</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="on_hold">On Hold</option>
        </select>
      </div>

      {filtered.length > 0 && (
        <p className="text-xs text-gray-400">{filtered.length} project{filtered.length !== 1 ? 's' : ''} found</p>
      )}

      {/* Projects List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-14 text-center">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No projects found</p>
          <p className="text-xs text-gray-400 mt-1">Try a different search or filter</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(project => {
            const progress = project.task_count > 0
              ? Math.round((project.completed_tasks / project.task_count) * 100)
              : 0;
            const isDelayed = project.status === 'active' && project.deadline && isPast(parseISO(project.deadline));
            const st = statusConfig[project.status] || statusConfig.active;

            return (
              <div
                key={project.id}
                onClick={() => navigate(`/client/projects/${project.id}`)}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:border-amber-300 cursor-pointer transition-all duration-200 p-5"
              >
                {/* Top: name + arrow */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900 text-base leading-tight">{project.name}</h3>
                      {project.project_id && (
                        <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md">
                          {project.project_id}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${st.bg} ${st.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                        {st.label}
                      </span>
                      {isDelayed && (
                        <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-600 font-medium">
                          <AlertTriangle className="w-3 h-3" /> Overdue
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-9 h-9 bg-amber-50 rounded-xl border border-amber-200 flex items-center justify-center">
                    <ArrowRight className="w-4 h-4 text-amber-600" />
                  </div>
                </div>

                <div className="border-t border-gray-100 my-3.5" />

                {/* Meta */}
                <div className="flex items-center gap-5 flex-wrap text-xs text-gray-500">
                  {project.deadline && (
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      Deadline:&nbsp;
                      <strong className={isDelayed ? 'text-red-600' : 'text-gray-700'}>
                        {safeDate(project.deadline)}
                      </strong>
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <Package className="w-3.5 h-3.5 text-gray-400" />
                    {project.item_count} item{project.item_count !== 1 ? 's' : ''}
                  </span>
                  {project.task_count > 0 && (
                    <span className="flex items-center gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                      {project.completed_tasks}/{project.task_count} stages done
                    </span>
                  )}
                </div>

                {/* Progress */}
                {project.task_count > 0 && (
                  <div className="mt-3.5">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-gray-400">Work Progress</span>
                      <span className="text-xs font-bold text-gray-700">{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          progress === 100 ? 'bg-emerald-500' :
                          progress >= 60  ? 'bg-amber-500' :
                          progress >= 30  ? 'bg-blue-500' : 'bg-slate-300'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
